import { Fragment, useRef, useCallback, useState, useEffect, useMemo } from 'react';
import { calcGST, round2 } from '@/utils/gst';
import type { InvoiceLineItem } from '@/types/billing';
import { emptyLineItem } from '@/types/billing';
import ItemSearchInput, { type SelectedItem } from '@/pages/billing/components/ItemSearchInput';
import { formatINR } from '@/utils/format';
import { useInvoiceTableNav } from '@/hooks/useInvoiceTableNav';
import { mockCategories } from '@/mocks/masters';
import { fetchBatchesForItem, type BatchStockRecord } from '@/api/batch.api';
import { useAuthStore } from '@/stores/authStore';
import { SerialSelectModal } from '../../components/SerialSelectModal';
import { useUnits, switchLineUnit } from '@/hooks/useUnits';
import LineUnitSelect from '@/components/common/LineUnitSelect';

interface Props {
  items: InvoiceLineItem[];
  onChange: (items: InvoiceLineItem[]) => void;
  isSameState: boolean;
  warehouseId?: string;
  rateType?: 'sale' | 'purchase';
  onAddNewItem?: (rowIdx: number) => void;
  /** Row indices that have qty > stock (highlighted red) */
  overstockRows?: Set<number>;
  /** Row indices that are missing required narration */
  missingNarrationRows?: Set<number>;
  itemLookup?: Array<{
    id: string;
    name: string;
    code?: string;
    isBatchTracked?: boolean;
    isSerialTracked?: boolean;
    [key: string]: any;
  }>;
}

const recalc = (item: InvoiceLineItem, isSameState: boolean): InvoiceLineItem => {
  const taxable = round2(item.qty * item.rate * (1 - item.discount / 100));
  const gst = calcGST(item.taxRate, taxable, isSameState);
  return { ...item, taxableAmount: taxable, ...gst, total: round2(taxable + gst.cgst + gst.sgst + gst.igst) };
};

const nfmt = (n: number) => (n === 0 ? '' : String(n));

function getCategoryForItem(categoryId?: string) {
  if (!categoryId) return null;
  return mockCategories.find((c) => c.id === categoryId) ?? null;
}

export default function InvoiceItemsTable({
  items,
  onChange,
  isSameState,
  warehouseId,
  rateType = 'sale',
  onAddNewItem,
  overstockRows = new Set(),
  missingNarrationRows = new Set(),
  itemLookup,
}: Props) {
  const tableRef = useRef<HTMLDivElement>(null);
  const [focusedRowIndex, setFocusedRowIndex] = useState(-1);
  // Line units, and the qty text while it is being typed ("2." must survive)
  const units = useUnits();
  const [qtyDraft, setQtyDraft] = useState<Record<string, string>>({});
  const clearQtyDraft = (id: string) =>
    setQtyDraft((d) => {
      if (!(id in d)) return d;
      const n = { ...d };
      delete n[id];
      return n;
    });
  const [batchesByItem, setBatchesByItem] = useState<Record<string, BatchStockRecord[]>>({});
  const [loadingBatches, setLoadingBatches] = useState<Record<string, boolean>>({});
  const [activeSerialPickerIdx, setActiveSerialPickerIdx] = useState<number | null>(null);
  const user = useAuthStore((s) => s.user);

  // Map of serial IDs reserved by other rows (to prevent duplicate selection in same invoice)
  const otherReservedSerials = useMemo(() => {
    const map: Record<string, { rowNum: number }> = {};
    items.forEach((it, idx) => {
      if (activeSerialPickerIdx !== null && idx === activeSerialPickerIdx) return;
      if (Array.isArray(it.serialIds)) {
        it.serialIds.forEach((sid) => {
          map[sid] = { rowNum: idx + 1 };
        });
      }
    });
    return map;
  }, [items, activeSerialPickerIdx]);

  // Track duplicate serial assignments across all rows in the form
  const duplicateSerialCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    items.forEach((it) => {
      if (Array.isArray(it.serialIds)) {
        it.serialIds.forEach((sid) => {
          counts[sid] = (counts[sid] || 0) + 1;
        });
      }
    });
    return counts;
  }, [items]);

  // Calculate remaining stock for a batch taking into account other rows for the same item
  const getBatchStockInfo = useCallback(
    (batch: BatchStockRecord, rowIdx: number, itemId: string) => {
      let reservedInOtherRows = 0;
      items.forEach((it, i) => {
        if (i === rowIdx || it.itemId !== itemId) return;
        if (it.batchId === batch.id || (it.batchNumber && it.batchNumber === batch.batchNumber)) {
          reservedInOtherRows += Number(it.qty) || 0;
        }
      });
      const remaining = Math.max(0, Number(batch.currentQty || 0) - reservedInOtherRows);
      return { remaining, reservedInOtherRows, totalStock: Number(batch.currentQty || 0) };
    },
    [items],
  );

  useEffect(() => {
    items.forEach((item) => {
      if (item.itemId && !batchesByItem[item.itemId] && !loadingBatches[item.itemId]) {
        setLoadingBatches((prev) => ({ ...prev, [item.itemId]: true }));
        fetchBatchesForItem(item.itemId, warehouseId)
          .then((batches) => {
            setBatchesByItem((prev) => ({ ...prev, [item.itemId]: batches }));
          })
          .catch(() => {
            setBatchesByItem((prev) => ({ ...prev, [item.itemId]: [] }));
          })
          .finally(() => {
            setLoadingBatches((prev) => ({ ...prev, [item.itemId]: false }));
          });
      }
    });
  }, [items, warehouseId, batchesByItem, loadingBatches]);

  const addRow = useCallback(() => {
    onChange([...items, emptyLineItem()]);
  }, [items, onChange]);

  const { handleCellKeyDown } = useInvoiceTableNav({
    tableRef: tableRef as React.RefObject<HTMLElement | null>,
    rowCount: items.length,
    onAddRow: addRow,
  });

  const update = useCallback(
    (idx: number, patch: Partial<InvoiceLineItem>) => {
      const next = items.map((it, i) => (i === idx ? recalc({ ...it, ...patch }, isSameState) : it));
      onChange(next);
    },
    [items, onChange],
  );

  const removeRow = useCallback(
    (idx: number) => {
      if (items.length <= 1) { onChange([emptyLineItem()]); return; }
      onChange(items.filter((_, i) => i !== idx));
    },
    [items, onChange],
  );

  // FIX 2: Delete/Backspace on empty row → delete row, focus previous row same col
  const handleEmptyRowDelete = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>, rowIdx: number, col: string) => {
      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      const row = items[rowIdx];
      const isEmpty = !row.itemId && !row.itemName && row.qty <= 0;
      if (isEmpty && items.length > 1) {
        e.preventDefault();
        removeRow(rowIdx);
        const prevIdx = Math.max(0, rowIdx - 1);
        setTimeout(() => {
          const el = tableRef.current?.querySelector<HTMLElement>(`[data-row="${prevIdx}"][data-col="${col}"]`);
          el?.focus();
        }, 30);
      }
      // Row has data → normal browser behaviour, don't intercept
    },
    [items, removeRow],
  );

  const handleItemSelect = (idx: number, selected: SelectedItem) => {
    const fullItem = itemLookup?.find((it) => it.id === selected.id);
    const isSerial = Boolean(selected.isSerialTracked ?? fullItem?.isSerialTracked);
    const isBatch = Boolean(selected.isBatchTracked ?? fullItem?.isBatchTracked);

    update(idx, {
      itemId: selected.id,
      itemCode: selected.code,
      itemName: selected.name,
      hsnCode: selected.hsnCode,
      taxRate: selected.taxRate,
      rate: rateType === 'sale' ? selected.saleRate : selected.purchaseRate,
      qty: 1,
      unit: selected.unit,
      unitId: selected.unitId,
      enteredUnitId: null,
      categoryId: selected.categoryId,
      narration: '',
      currentStock: selected.currentStock,
      isBatchTracked: isBatch,
      isSerialTracked: isSerial,
      batchNumber: null,
      batchId: null,
      autoFIFO: true,
      serialNumbers: '',
      serialIds: [],
    });
    setTimeout(() => {
      const qtyInput = tableRef.current?.querySelector(`[data-row="${idx}"][data-col="qty"]`) as HTMLInputElement;
      qtyInput?.focus();
    }, 50);
  };

  // Focus/blur tracking for stock badge
  const handleRowFocus = useCallback((rowIdx: number) => {
    setFocusedRowIndex(rowIdx);
  }, []);

  const handleRowBlur = useCallback((rowIdx: number) => {
    setTimeout(() => {
      const active = document.activeElement;
      const rowEl = tableRef.current?.querySelector(`[data-row-container="${rowIdx}"]`);
      if (!rowEl?.contains(active)) {
        setFocusedRowIndex((prev) => (prev === rowIdx ? -1 : prev));
      }
    }, 50);
  }, []);

  const numIn = (isOverstock: boolean) =>
    `w-full h-8 text-right text-sm bg-transparent focus:bg-indigo-50 focus:outline-none focus:ring-1 focus:ring-inset rounded px-1 ${
      isOverstock ? 'focus:ring-red-300 ring-1 ring-red-400 bg-red-50' : 'focus:ring-indigo-300'
    }`;
  const calcCell = 'text-right text-sm text-[#1e293b] pr-2';

  // Stock badge renderer
  const renderStockBadge = (row: InvoiceLineItem) => {
    if (!row.itemId) return null;
    const stock = row.currentStock ?? 0;
    if (stock > 10) {
      return (
        <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-medium whitespace-nowrap">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
          Stock: {stock} {row.unit || 'pcs'} available
        </span>
      );
    }
    if (stock > 0) {
      return (
        <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 font-medium whitespace-nowrap">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
          Only {stock} {row.unit || 'pcs'} — low stock
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-200 font-medium whitespace-nowrap">
        <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
        Out of stock in your warehouse
      </span>
    );
  };

  // Narration requirement check
  const categoryRequiresNarration = (categoryId?: string) => {
    if (!categoryId) return false;
    const cat = getCategoryForItem(categoryId);
    return cat?.requiresNarration ?? false;
  };

  void user; // used for future permission checks

  return (
    <div ref={tableRef} className="w-full overflow-x-auto">
      <table className="w-full min-w-[860px] text-sm border-collapse">
        <thead>
          <tr className="bg-slate-50 border-b border-[#e2e8f0]">
            {['#', 'Item / Barcode', 'HSN', 'Qty', 'Unit', 'Rate', 'Disc%', 'Taxable', 'GST%', 'Total', ''].map((h) => (
              <th
                key={h}
                className={`px-2 py-2.5 text-left text-xs font-semibold text-slate-500 ${
                  ['Qty', 'Rate', 'Disc%', 'Taxable', 'GST%', 'Total'].includes(h) ? 'text-right' : ''
                }`}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((row, idx) => {
            const isOverstock = overstockRows.has(idx);
            const isMissingNarration = missingNarrationRows.has(idx);
            const isFocused = focusedRowIndex === idx;
            const needsNarration = categoryRequiresNarration(row.categoryId);
            const category = getCategoryForItem(row.categoryId);

            const serialCount = (row.serialNumbers || '')
              .split(/[,\n]+/)
              .map((s) => s.trim())
              .filter(Boolean).length;
            const availableBatches = (row.itemId ? batchesByItem[row.itemId] : []) || [];
            const shouldShowBatchRow = Boolean(row.isBatchTracked);

            return (
              <Fragment key={row.id}>
                <tr
                  data-row-container={idx}
                  className={`border-b border-slate-100 group transition-colors ${
                    isOverstock ? 'bg-red-50/40' : isFocused ? 'bg-indigo-50/30' : 'hover:bg-slate-50/50'
                  }`}
                >
                  <td className="px-2 py-1 w-8 text-center text-xs text-slate-400">{idx + 1}</td>

                  {/* Item search + stock badge + narration */}
                  <td className="px-2 py-1 min-w-[200px]" data-row={idx} data-col="item">
                    <ItemSearchInput
                      value={row.itemName}
                      onChange={(v) => update(idx, { itemName: v })}
                      onSelect={(s) => handleItemSelect(idx, s)}
                      onKeyDown={(e) => {
                        handleEmptyRowDelete(e, idx, 'item');
                        handleCellKeyDown(e, idx, 'item');
                      }}
                      warehouseId={warehouseId}
                      rateType={rateType}
                      placeholder="Type or scan barcode..."
                      onAddNew={onAddNewItem ? () => onAddNewItem(idx) : undefined}
                      onFocus={() => handleRowFocus(idx)}
                      onBlur={() => handleRowBlur(idx)}
                    />
                    {/* Item Tracking Badges */}
                    {/* {row.itemId && (
                      <div className="flex items-center gap-1.5 mt-1">
                        {row.isSerialTracked && (
                          <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 font-semibold">
                            <i className="ri-barcode-line text-[10px]" />
                            Serial Tracked
                          </span>
                        )}
                        {row.isBatchTracked && (
                          <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 font-semibold">
                            <i className="ri-archive-line text-[10px]" />
                            Batch Tracked
                          </span>
                        )}
                      </div>
                    )} */}
                    {/* Stock badge — only on focused row with item selected */}
                    {isFocused && row.itemId && (
                      <div className="mt-0.5">{renderStockBadge(row)}</div>
                    )}
                    {/* Narration field for categories that require it */}
                    {needsNarration && row.itemId && (
                      <div className="mt-1">
                        <input
                          type="text"
                          value={row.narration ?? ''}
                          onChange={(e) => update(idx, { narration: e.target.value })}
                          onFocus={() => handleRowFocus(idx)}
                          onBlur={() => handleRowBlur(idx)}
                          placeholder={`Serial/IMEI (required for ${category?.name ?? 'this category'})`}
                          className={`w-full h-7 px-2 text-xs rounded border focus:outline-none focus:ring-1 ${
                            isMissingNarration
                              ? 'border-red-400 bg-red-50 focus:ring-red-300 placeholder:text-red-400'
                              : 'border-[#e2e8f0] bg-white focus:border-[#4f46e5] focus:ring-indigo-200'
                          }`}
                        />
                      </div>
                    )}
                  </td>

                  {/* HSN */}
                  <td className="px-2 py-1 w-24" data-row={idx} data-col="hsn">
                    <input
                      data-row={idx}
                      data-col="hsn"
                      value={row.hsnCode}
                      onChange={(e) => update(idx, { hsnCode: e.target.value })}
                      onKeyDown={(e) => {
                        handleEmptyRowDelete(e, idx, 'hsn');
                        handleCellKeyDown(e, idx, 'hsn');
                      }}
                      onFocus={() => handleRowFocus(idx)}
                      onBlur={() => handleRowBlur(idx)}
                      className="w-full h-8 text-sm bg-transparent focus:bg-indigo-50 focus:outline-none focus:ring-1 focus:ring-inset focus:ring-indigo-300 rounded px-1"
                      placeholder="HSN"
                    />
                  </td>

                  {/* Qty */}
                  <td className="px-2 py-1 w-20">
                    <input
                      type="text"
                      inputMode="decimal"
                      data-row={idx}
                      data-col="qty"
                      value={qtyDraft[row.id] ?? nfmt(row.qty)}
                      placeholder="0"
                      onChange={(e) => {
                        // Decimals only in units that allow them (Kg yes, Pcs no)
                        const value = e.target.value;
                        const lineUnit = units.byId(row.enteredUnitId) ?? units.byCode(row.unit);
                        const pattern = lineUnit && !lineUnit.allowDecimal ? /^\d*$/ : /^\d*\.?\d{0,6}$/;
                        if (pattern.test(value)) {
                          setQtyDraft((d) => ({ ...d, [row.id]: value }));
                          update(idx, { qty: parseFloat(value) || 0 });
                        }
                      }}
                      onBlur={() => {
                        clearQtyDraft(row.id);
                        handleRowBlur(idx);
                      }}
                      onKeyDown={(e) => {
                        handleEmptyRowDelete(e, idx, 'qty');
                        handleCellKeyDown(e, idx, 'qty');
                      }}
                      onFocus={() => handleRowFocus(idx)}
                      className={numIn(isOverstock)}
                    />
                  </td>

                  {/* Unit: related units of the stock unit (Kg, g, Ton). Batch / serial items stay in the
                      stock unit so batch quantities and serial counts match the line one to one. */}
                  <td className="px-2 py-1 w-16 text-xs text-slate-500 text-center">
                    {row.unit && !row.isBatchTracked && !row.isSerialTracked ? (
                      <LineUnitSelect
                        catalog={units}
                        stockUnit={row.unit}
                        value={row.enteredUnitId}
                        onChange={(unitId) => {
                          const next = switchLineUnit(units, row.unit, row.enteredUnitId, unitId, row.qty, row.rate);
                          clearQtyDraft(row.id);
                          update(idx, { enteredUnitId: unitId, qty: next.qty, rate: next.rate ?? row.rate });
                        }}
                        className="h-7 w-full px-1 text-xs bg-transparent border border-transparent hover:border-[#e2e8f0] rounded focus:outline-none focus:border-[#4f46e5] cursor-pointer"
                      />
                    ) : (
                      row.unit || '—'
                    )}
                  </td>

                  {/* Rate */}
                  <td className="px-2 py-1 w-24">
                    <input
                      type="text"
                      inputMode="decimal"
                      pattern="[0-9]*[.]?[0-9]*"
                      data-row={idx}
                      data-col="rate"
                      value={nfmt(row.rate)}
                      placeholder="0.00"
                      onChange={(e) => {
                        const value = e.target.value;
                        if (/^\d*(\.\d*)?$/.test(value)) {
                          update(idx, { rate: parseFloat(value) || 0 });
                        }
                      }}
                      onBlur={() => handleRowBlur(idx)}
                      onKeyDown={(e) => {
                        handleEmptyRowDelete(e, idx, 'rate');
                        handleCellKeyDown(e, idx, 'rate');
                      }}
                      onFocus={() => handleRowFocus(idx)}
                      className={numIn(isOverstock)}
                    />
                  </td>

                  {/* Disc% */}
                  <td className="px-2 py-1 w-16">
                    <input
                      type="text"
                      inputMode="decimal"
                      pattern="[0-9]*[.]?[0-9]*"
                      data-row={idx}
                      data-col="discount"
                      value={nfmt(row.discount)}
                      placeholder="0"
                      onChange={(e) => {
                        const value = e.target.value;
                        if (/^\d*(\.\d*)?$/.test(value)) {
                          update(idx, { discount: Math.min(100, Math.max(0, parseFloat(value) || 0)) });
                        }
                      }}
                      onBlur={() => handleRowBlur(idx)}
                      onKeyDown={(e) => {
                        handleEmptyRowDelete(e, idx, 'discount');
                        handleCellKeyDown(e, idx, 'discount');
                      }}
                      onFocus={() => handleRowFocus(idx)}
                      className="w-full h-8 text-right text-sm bg-transparent focus:bg-indigo-50 focus:outline-none focus:ring-1 focus:ring-inset focus:ring-indigo-300 rounded px-1"
                    />
                  </td>

                  {/* Taxable (read-only) */}
                  <td className={`px-2 py-1 w-24 ${calcCell}`}>
                    {row.taxableAmount > 0 ? formatINR(row.taxableAmount) : '—'}
                  </td>

                  {/* GST% (read-only) */}
                  <td className="px-2 py-1 w-14 text-right text-xs text-slate-500">
                    {row.taxRate > 0 ? `${row.taxRate}%` : '—'}
                  </td>

                  {/* Total (read-only) */}
                  <td className={`px-2 py-1 w-24 ${calcCell} font-semibold`}>
                    {row.total > 0 ? formatINR(row.total) : '—'}
                  </td>

                  {/* Delete */}
                  <td className="w-8 text-center">
                    <button
                      type="button"
                      onClick={() => removeRow(idx)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity w-6 h-6 flex items-center justify-center text-red-400 hover:text-red-600 cursor-pointer"
                    >
                      <i className="ri-delete-bin-6-line text-sm" />
                    </button>
                  </td>
                </tr>

                {/* ── Batch Tracking: Auto-FIFO or Specific Batch ── */}
                {shouldShowBatchRow && (
                  <tr>
                    <td colSpan={11} className="px-2 pb-2">
                      <div className="p-2.5 bg-amber-50/70 border border-amber-200 rounded-lg flex flex-col md:flex-row md:items-center justify-between gap-2.5">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-amber-900 flex items-center gap-1">
                            <i className="ri-archive-line text-amber-600" />
                            Batch Tracking:
                          </span>

                          {/* Toggle Auto-FIFO vs Specific Batch */}
                          <div className="inline-flex rounded-lg p-0.5 bg-amber-100 border border-amber-200 text-xs">
                            <button
                              type="button"
                              onClick={() =>
                                update(idx, {
                                  autoFIFO: true,
                                  batchNumber: null,
                                  batchId: null,
                                })
                              }
                              className={`px-2.5 py-1 rounded-md font-medium transition-colors cursor-pointer ${
                                row.autoFIFO !== false
                                  ? 'bg-white text-indigo-700 shadow-sm font-semibold'
                                  : 'text-amber-800 hover:text-amber-950'
                              }`}
                            >
                              Auto-FIFO (Recommended)
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                const first = availableBatches[0];
                                update(idx, {
                                  autoFIFO: false,
                                  batchNumber: first?.batchNumber || null,
                                  batchId: first?.id || null,
                                  mfgDate: first?.mfgDate || null,
                                  expiryDate: first?.expiryDate || null,
                                });
                              }}
                              className={`px-2.5 py-1 rounded-md font-medium transition-colors cursor-pointer ${
                                row.autoFIFO === false
                                  ? 'bg-white text-indigo-700 shadow-sm font-semibold'
                                  : 'text-amber-800 hover:text-amber-950'
                              }`}
                            >
                              Choose Specific Batch
                            </button>
                          </div>
                        </div>

                        {/* If Auto-FIFO */}
                        {row.autoFIFO !== false ? (
                          <div className="flex items-center gap-2 text-xs text-amber-800">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-100/80 text-emerald-800 border border-emerald-300 font-medium">
                              <i className="ri-check-line text-emerald-600" />
                              Auto Allocation
                            </span>
                            <span>
                              {availableBatches.length > 0 ? (
                                (() => {
                                  const totalStock = availableBatches.reduce((s, b) => s + Number(b.currentQty || 0), 0);
                                  let totalReservedByOther = 0;
                                  items.forEach((it, i) => {
                                    if (i !== idx && it.itemId === row.itemId) {
                                      totalReservedByOther += Number(it.qty) || 0;
                                    }
                                  });
                                  const unreserved = Math.max(0, totalStock - totalReservedByOther);
                                  return (
                                    <>
                                      Oldest batches will be consumed first ({unreserved} of {totalStock} {row.unit || 'pcs'} available across {availableBatches.length} batch(es)
                                      {totalReservedByOther > 0 ? ` • ${totalReservedByOther} reserved in other rows` : ''}).
                                    </>
                                  );
                                })()
                              ) : (
                                <>No active batches currently in stock for this warehouse.</>
                              )}
                            </span>
                          </div>
                        ) : (
                          /* If Specific Batch Selected */
                          <div className="flex items-center gap-2 flex-1 max-w-xl">
                            <select
                              value={row.batchNumber || ''}
                              onChange={(e) => {
                                const selectedBatchNo = e.target.value;
                                const matched = availableBatches.find(
                                  (b) => b.batchNumber === selectedBatchNo,
                                );
                                update(idx, {
                                  autoFIFO: false,
                                  batchNumber: selectedBatchNo || null,
                                  batchId: matched?.id || null,
                                  mfgDate: matched?.mfgDate || null,
                                  expiryDate: matched?.expiryDate || null,
                                });
                              }}
                              className="border border-amber-300 rounded px-2.5 py-1 text-xs flex-1 bg-white focus:outline-none focus:ring-1 focus:ring-amber-500 font-mono"
                            >
                              <option value="">Select a batch...</option>
                              {availableBatches.map((b) => {
                                const { remaining, reservedInOtherRows, totalStock } = getBatchStockInfo(b, idx, row.itemId);
                                const isExhaustedByOther = remaining <= 0 && b.batchNumber !== row.batchNumber;
                                return (
                                  <option key={b.id} value={b.batchNumber} disabled={isExhaustedByOther}>
                                    {b.batchNumber} (
                                      {reservedInOtherRows > 0 ? `Available: ${remaining} of ${totalStock}` : `Stock: ${totalStock}`} {row.unit || 'pcs'}
                                      {reservedInOtherRows > 0 ? ` [${reservedInOtherRows} reserved in other row(s)]` : ''}
                                      {b.mfgDate ? ` | Mfg: ${b.mfgDate.slice(0, 10)}` : ''}
                                      {b.expiryDate ? ` | Exp: ${b.expiryDate.slice(0, 10)}` : ' | No Expiry'}
                                    )
                                  </option>
                                );
                              })}
                            </select>
                            {(() => {
                              const chosen = availableBatches.find((b) => b.batchNumber === row.batchNumber);
                              if (chosen) {
                                const { remaining, reservedInOtherRows } = getBatchStockInfo(chosen, idx, row.itemId);
                                if (remaining < Number(row.qty || 0)) {
                                  return (
                                    <span className="text-[11px] font-medium text-red-600 bg-red-50 border border-red-200 px-2 py-0.5 rounded whitespace-nowrap flex items-center gap-1">
                                      <i className="ri-alert-line text-xs" />
                                      Only {remaining} available in this batch{reservedInOtherRows > 0 ? ` (${reservedInOtherRows} reserved in other row)` : ''}!
                                    </span>
                                  );
                                }
                              }
                              return null;
                            })()}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}

                {/* ── Serial Tracking Sub-Row ── */}
                {row.isSerialTracked && (
                  <tr>
                    <td colSpan={11} className="px-2 pb-2">
                      <div className="p-2.5 bg-blue-50/80 border border-blue-200 rounded-lg space-y-2">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-semibold text-blue-900 flex items-center gap-1">
                              <i className="ri-qr-code-line text-blue-600" />
                              Serial Tracking:
                            </span>
                            <button
                              type="button"
                              onClick={() => setActiveSerialPickerIdx(idx)}
                              className={`px-3 py-1 rounded-md text-xs font-semibold flex items-center gap-1.5 shadow-sm transition-all cursor-pointer ${
                                (row.serialIds || []).length === Number(row.qty) && Number(row.qty) > 0
                                  ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                                  : 'bg-blue-600 hover:bg-blue-700 text-white'
                              }`}
                            >
                              <i className="ri-barcode-line text-sm" />
                              {(row.serialIds || []).length === Number(row.qty) && Number(row.qty) > 0
                                ? `✓ Picked ${(row.serialIds || []).length}/${row.qty} Serials`
                                : `Select / Scan Serials (${(row.serialIds || []).length || serialCount}/${row.qty || 0})`}
                            </button>
                          </div>

                          <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${
                            ((row.serialIds || []).length === Number(row.qty) || serialCount === Number(row.qty)) && Number(row.qty) > 0
                              ? 'bg-emerald-100 text-emerald-800'
                              : 'bg-amber-100 text-amber-800'
                          }`}>
                            {((row.serialIds || []).length || serialCount)} of {row.qty || 0} assigned
                          </span>
                        </div>

                        {/* Display assigned serial numbers preview */}
                        {row.serialNumbers && (
                          <div className="flex flex-wrap gap-1 pt-1">
                            {row.serialNumbers.split(/[,\n]+/).map((s, sIdx) => {
                              const trimmed = s.trim();
                              // Check if duplicate across rows
                              const isDup = row.serialIds?.some(sid => (duplicateSerialCounts[sid] || 0) > 1);
                              return (
                                <span
                                  key={sIdx}
                                  className={`px-2 py-0.5 rounded text-[10px] font-mono font-medium border ${
                                    isDup
                                      ? 'bg-rose-100 text-rose-800 border-rose-300 font-bold'
                                      : 'bg-white text-blue-800 border border-blue-200'
                                  }`}
                                >
                                  {trimmed}
                                  {isDup && ' ⚠️ Conflict'}
                                </span>
                              );
                            })}
                          </div>
                        )}

                        {row.serialIds?.some(sid => (duplicateSerialCounts[sid] || 0) > 1) && (
                          <p className="text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-2.5 py-1 font-semibold flex items-center gap-1">
                            <i className="ri-error-warning-fill text-rose-600 text-sm" />
                            Duplicate serial unit conflict! One or more serials selected here are also assigned to another row. Please re-pick unique serials.
                          </p>
                        )}

                        {Number(row.qty) > 0 && ((row.serialIds || []).length || serialCount) !== Number(row.qty) && (
                          <p className="text-[11px] text-rose-600 font-medium flex items-center gap-1">
                            <i className="ri-error-warning-line" />
                            Please pick exactly {row.qty} serial number(s) to match the item quantity before saving.
                          </p>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
      <button
        type="button"
        onClick={addRow}
        className="mt-2 ml-2 flex items-center gap-1.5 text-sm text-[#4f46e5] hover:text-indigo-700 font-medium cursor-pointer py-1.5 px-2 rounded-lg hover:bg-indigo-50 transition-colors"
      >
        <i className="ri-add-line text-base" /> Add Row
      </button>

      {/* Serial Selection Modal */}
      {activeSerialPickerIdx !== null && items[activeSerialPickerIdx] && (
        <SerialSelectModal
          isOpen={true}
          onClose={() => setActiveSerialPickerIdx(null)}
          itemId={items[activeSerialPickerIdx].itemId}
          itemName={items[activeSerialPickerIdx].itemName}
          warehouseId={warehouseId}
          requiredQty={Number(items[activeSerialPickerIdx].qty) || 1}
          selectedSerialIds={items[activeSerialPickerIdx].serialIds || []}
          reservedSerialIds={otherReservedSerials}
          onConfirm={(chosenSerials) => {
            const snString = chosenSerials.map((s) => s.serial_number).join(', ');
            update(activeSerialPickerIdx, {
              serialIds: chosenSerials.map((s) => s.id),
              serialNumbers: snString,
            });
            setActiveSerialPickerIdx(null);
          }}
        />
      )}
    </div>
  );
}
